import { MouseEvent, RefObject, TouchEvent, useState } from 'react'

export function usePlayerSeek(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  canSeek?: boolean,
  onSeek?: (progress: number) => void
) {
  const [isSeeking, setIsSeeking] = useState(false)
  const [seekProgress, setSeekProgress] = useState<number>(0)

  const determineProgress = (clientX: number) => {
    const { current } = canvasRef

    if (!current) {
      return 0
    }

    const context = current.getContext('2d')

    if (!context) {
      return 0
    }

    let percent = 0

    if (clientX < current.getBoundingClientRect().left) {
      percent = 0
    } else if (clientX > current.getBoundingClientRect().right) {
      percent = 1
    } else {
      percent =
        (clientX - current.getBoundingClientRect().left) / current.clientWidth
    }

    return percent
  }

  const onMove = (clientX: number) => {
    if (!canSeek || !isSeeking) {
      return
    }
    const percent = determineProgress(clientX)
    setSeekProgress(percent)
  }

  const onStartInteraction = (clientX: number) => {
    if (!canSeek) {
      return
    }
    setIsSeeking(true)
    const percent = determineProgress(clientX)
    setSeekProgress(percent)
  }

  const onEndInteraction = (clientX: number) => {
    if (!canSeek || !isSeeking) {
      return
    }

    setIsSeeking(false)

    if (onSeek) {
      const percent = determineProgress(clientX)
      onSeek(percent)
    }
  }

  const onMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    onStartInteraction(e.clientX)
  }

  const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    onMove(e.clientX)
  }

  const onMouseLeave = (e: MouseEvent<HTMLDivElement>) => {
    onEndInteraction(e.clientX)
  }

  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    const { clientX } = e.changedTouches[0]
    onMove(clientX)
  }

  const onMouseUp = (e: MouseEvent<HTMLDivElement>) => {
    onEndInteraction(e.clientX)
  }

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const { clientX } = e.changedTouches[0]
    onStartInteraction(clientX)
  }

  const onTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const { clientX } = e.changedTouches[0]
    onEndInteraction(clientX)
  }

  const onTouchCancel = (e: TouchEvent<HTMLDivElement>) => {
    const { clientX } = e.changedTouches[0]
    onEndInteraction(clientX)
  }

  return {
    seekProgress,
    isSeeking,
    onMouseDown,
    onMouseMove,
    onMouseLeave,
    onTouchMove,
    onMouseUp,
    onTouchStart,
    onTouchEnd,
    onTouchCancel,
  }
}
